'use client';

import { ErrorOutline, HelpOutline } from '@mui/icons-material';
import { IconButton, Skeleton, Stack, SxProps, Theme, Tooltip, Typography } from '@mui/material';
import { formatToDDMMMYYYYHHMMAAA } from '@verifime/utils';
import { ReactElement, ReactNode } from 'react';
import CardContainer from '../CardContainer/CardContainer';
import ConfidenceGuidance from './ConfidenceGuidance';
import { UNAVAILABLE_STATEMENT, UNAVAILABLE_LEVEL_NAME } from './statements';

/**
 * Which of the three things a card can be showing.
 *
 * These are not three levels. A level is a fact about the customer's checks; `loading` and `error`
 * are facts about our request for it. Conflating them is the defect this exists to prevent: a card
 * that renders `NotCompleted` while a call is still in flight tells a client their customer has not
 * done something, which may be untrue and is not recoverable by looking harder.
 */
export type TConfidenceCardStatus = 'ready' | 'loading' | 'error';

/** What stands in for the timestamp on error, matching `EvidenceProvenance`'s convention. */
const MISSING_TIMESTAMP = '-';

export type TConfidenceCardLayoutProps = {
  heading: string;
  helpText: ReactNode;
  /** Omitted while loading and on error, when there is no level to draw. */
  meter?: ReactElement;
  levelName?: string;
  /**
   * What happened: the prose that follows `levelName` in the same paragraph. Named `statement`,
   * not `guidance`, to keep it distinct from the `guidance` prop below. The two used to share a
   * name, which is precisely how the next-step sentence was lost off this card in the first place
   * (see `statements.ts`'s docblock).
   */
  statement?: string;
  /**
   * What to do next: reason-free next-step prose rendered in its own tinted box below the
   * statement paragraph, via `ConfidenceGuidance`. Omitted entirely for the levels that need no
   * next step and for the `error` branch, where there is no level to act on.
   */
  guidance?: ReactNode;
  /** Defaults to `ready`, so an existing caller that passes a level need not opt in. */
  status?: TConfidenceCardStatus;
  actions?: ReactNode;
  /**
   * A statement rendered where the action row sits, for a state in which there is nothing left to
   * act on.
   *
   * **Not `guidance`.** That box is the document axis's compliance-controlled next step (ADR 264),
   * populated from the level. This is about the reader's own standing rather than the customer's
   * check, so it takes a region of its own rather than a third sentence in one that is not allowed
   * to grow.
   *
   * **Rendered at `ready` only**, alongside the actions and the media and for the same reason: a
   * card with no level has nothing to explain, and the failure treatment already says what it
   * needs to.
   *
   * The caller is expected to pass this *instead of* `actions`, not alongside them — a card that
   * still offers a control does not need to be told what it cannot do. Both render if both arrive
   * rather than one silently dropping the other, because a caller that passes both has a bug worth
   * seeing rather than hiding.
   */
  notice?: ReactNode;
  media?: ReactNode;
  /** Omitted while loading and on error, when there is no determination to date. */
  lastUpdatedOn?: string;
  sx?: SxProps<Theme>;
  /**
   * The card's own testid, passed straight through to the root element this renders.
   *
   * Kept where the meters, the captures column and the standalone controls all had theirs removed,
   * because this is not a second identifier: this component *is* the card's root element, so the id
   * arriving here is the one page-unique value ADR 72 asks a parent component to carry. Removing it
   * would leave `DocumentConfidenceCard` and `BiometricConfidenceCard` with no root to put theirs
   * on, since neither renders an element of its own.
   */
  'data-testid'?: string;
};

/**
 * The shape shared by every confidence check card: heading with a help tooltip, the axis's meter,
 * a bold level name leading straight into its statement prose in one paragraph, an optional
 * next-step guidance box beneath that, an optional actions row, and a "Last updated on" timestamp
 * at the foot, with an optional media slot beside the lot.
 *
 * **Built to sit in a row of cards.** It takes `flex: 1` and stretches to its neighbour's height,
 * and pins its timestamp to the foot of that height, so two cards side by side are the same size
 * and their timestamps line up. A caller placing one on its own loses nothing by this.
 *
 * **Deliberately knows nothing about which axis it is showing**, the same reason `SegmentedMeter`
 * sits underneath the two axis-specific meters. `DocumentConfidenceCard` and
 * `BiometricConfidenceCard` own the heading text, the tooltip copy, and the level-to-name,
 * level-to-statement and level-to-guidance lookups, and pass the results in as a plain string and
 * nodes. Not exported from the package barrel for the same reason `SegmentedMeter` is not: a call
 * site reaching for it directly would be re-deriving which heading, meter, name, statement and
 * guidance belong together, which is exactly what the two card components exist to fix in one
 * place.
 */
export default function ConfidenceCardLayout({
  heading,
  helpText,
  meter,
  levelName,
  statement,
  guidance,
  status = 'ready',
  actions,
  notice,
  media,
  lastUpdatedOn,
  sx,
  'data-testid': dataTestId,
}: Readonly<TConfidenceCardLayoutProps>): ReactElement {
  const isLoading = status === 'loading';
  const isError = status === 'error';

  return (
    <CardContainer
      // Without this the loading card is, to a screen reader, a heading and a caption with nothing
      // to say: indistinguishable from a card that simply has no data. Matches the house pattern in
      // `CustomerLookup`, `OrganisationUserProvision` and `OrganisationNotification`.
      aria-busy={isLoading}
      data-testid={dataTestId}
      sx={[
        (t: Theme) => ({
          padding: `${t.semantic.spacing.base} ${t.semantic.spacing.lg}`,
          // No border set here: `CardContainer` completes its own themed border by default, so
          // this card inherits `borderStyle: 'solid'` and
          // `borderColor: theme.palette.divider` rather than repeating them. Confirmed the
          // computed value is unchanged: 1px solid rgba(0, 0, 0, 0.12) in light and
          // rgba(255, 255, 255, 0.12) in dark, both before and after.
          // `flex: 1` here rather than at the call site, so two cards placed in a row share it
          // equally whoever composes them. `minWidth: 0` stops the longer guidance paragraph
          // widening its own card past its share: without it a flex item refuses to shrink below
          // its content, and the axis with more prose quietly takes the row.
          flex: 1,
          minWidth: 0,
          // A row, so the media slot sits beside the text rather than under it. With no media
          // this is a row of one full-width child, which lays out exactly as a column would.
          display: 'flex',
          flexDirection: 'row',
          gap: t.semantic.spacing.base,
        }),
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Stack sx={(t: Theme) => ({ flex: 1, minWidth: 0, gap: t.card.contentGap })}>
        <Stack sx={(t: Theme) => ({ gap: t.card.itemGap })}>
          <Stack
            direction="row"
            alignItems="center"
            sx={(t: Theme) => ({ gap: t.semantic.spacing.xs })}
          >
            <Typography variant="body1">{heading}</Typography>
            {/* `HelpOutline` alone is a bare `SvgIcon`, which MUI defaults to `aria-hidden`, so
                the tooltip's text had no route to assistive technology at all: not by keyboard,
                since there was nothing in the tab order, and not by screen reader, since the
                hidden icon carried no name for one to announce. Wrapped in an `IconButton` for the
                same focusable-named-control shape used elsewhere in this package (see
                `ZoomableImageFrame`'s zoom controls). The label is derived from `heading` rather
                than a single fixed string, so the document and biometric cards do not announce as
                the same control.

                `data-action` follows the same naming family as this file's other reveal-style
                controls (`view-evidence`, `view-assessment`): this one reveals the tooltip rather
                than a dialog or a document, so `view-help` names what it does without implying a
                navigation. No `data-testid` here: ADR 72 asks for `data-action` on an interactive
                control, not a second identifier alongside it, so a test selects this button by its
                accessible name or by `[data-action="view-help"]`. */}
            <Tooltip title={helpText} placement="right" arrow>
              <IconButton
                size="small"
                aria-label={`${heading} help`}
                data-action="view-help"
                sx={(t: Theme) => ({ padding: 0, color: t.palette.text.secondary, cursor: 'help' })}
              >
                <HelpOutline fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {/* The meter's footprint is held by a skeleton of the same size while loading, and left
              out entirely on error. Sized from SegmentedMeter's own defaults rather than a literal,
              so the placeholder cannot drift away from the thing it stands in for. */}
          {isLoading ? (
            <Skeleton
              variant="rounded"
              sx={(t: Theme) => ({
                width: t.semantic.size['3xs'],
                height: t.semantic.spacing['2xs'],
              })}
            />
          ) : (
            !isError && meter
          )}

          {isLoading ? (
            // Two lines, because every level's statement runs to at least two and a single-line
            // placeholder would make the card visibly grow when the real prose arrives.
            <Stack sx={(t: Theme) => ({ gap: t.semantic.spacing['3xs'] })}>
              <Skeleton variant="text" width="100%" />
              <Skeleton variant="text" width="60%" />
            </Stack>
          ) : (
            <Stack
              direction="row"
              alignItems="flex-start"
              sx={(t: Theme) => ({ gap: t.semantic.spacing['2xs'] })}
            >
              {/* Decorative: the words carry the meaning on their own, so the icon is never the
                  sole signal and takes aria-hidden rather than a label that would announce the
                  state a second time. */}
              {isError && (
                <ErrorOutline
                  aria-hidden
                  fontSize="small"
                  sx={(t: Theme) => ({ color: t.palette.text.secondary })}
                />
              )}
              <Typography
                variant="body2"
                sx={(t: Theme) => ({ textWrap: 'pretty', lineHeight: t.semantic.lineHeight.long })}
              >
                <Typography
                  component="span"
                  variant="body2"
                  sx={(t: Theme) => ({ fontWeight: t.semantic.fontWeight.semibold })}
                >
                  {isError ? UNAVAILABLE_LEVEL_NAME : levelName}
                </Typography>
                {`. ${isError ? UNAVAILABLE_STATEMENT : statement}`}
              </Typography>
            </Stack>
          )}

          {/* The next-step guidance box. Never rendered on error: with no level there is no
              guidance to give, and `ConfidenceGuidance` is reason-free by design, so it has no way
              to say "we don't know" instead of a genuine next step. */}
          {!isLoading && !isError && guidance && (
            <ConfidenceGuidance data-testid={dataTestId ? `${dataTestId}-guidance` : undefined}>
              {guidance}
            </ConfidenceGuidance>
          )}

          {/* Three different rows, one per state.

              Loading holds the button row's footprint so the card does not grow much when the real
              buttons arrive. It reserves one button row, not the whole slot: the biometric card puts
              the request link on a second row beneath. The media slot is not reserved either, though
              it now costs less than it did: media sits beside the content rather than under it, so
              a capture arriving widens the card's own column rather than making the card taller.
              It cannot render the buttons themselves: `VIEW EVIDENCE` is
              filled only at `Low` and the request verb turns on whether a task is open, so before
              the data lands there is no correct way to draw either. A neutral default would be the
              same lie as falling back to a level, with a click attached to it.

              Error renders nothing at all, and neither does the media slot. The level is the hinge:
              with no level there is nothing to view, and offering a request button anyway would put
              a control in front of a client that emails their customer. A button that exists gets
              pressed, and the customer bothered may be one whose confidence was high all along. */}
          {isLoading && (
            // One placeholder for the region, not one per button. A count would restate what the
            // ready state renders, in a second place that nothing keeps in step: the document card
            // offers its single button only at `Low`, and the biometric card's controls vary with
            // whether a task is open, so any number written here is wrong for most levels and would
            // go quietly stale the first time an action set changed.
            <Skeleton
              variant="rounded"
              sx={(t: Theme) => ({ width: '60%', height: t.semantic.size['5xs'] })}
            />
          )}

          {status === 'ready' && actions && (
            // No `data-testid` on this row: ADR 72 asks for `data-action` on the controls
            // themselves rather than a second identifier on their container. A test scopes to
            // what this row holds by filtering for `[data-action]` other than `view-help`, which
            // is the heading's help control beside it, not one of these actions.
            <Stack direction="row" sx={(t: Theme) => ({ gap: t.semantic.spacing.xs })}>
              {actions}
            </Stack>
          )}

          {/* Beneath the actions row rather than replacing it, so a caller that passes both is
              visibly wrong instead of quietly losing one. In the state this exists for there are
              no actions at all, so the row above is not rendered and this sits directly under the
              statement paragraph — which is where the control it replaces used to be. */}
          {status === 'ready' && notice}
        </Stack>

        {/* Sibling of the content stack above, not its last child: the prototype renders the
            timestamp as its own two-line block (a "Last updated on" caption over the date),
            a caption over its value rather than one concatenated sentence.

            The caption is kept in all three states and only the value is replaced, so the block
            holds its height and the card does not resize as data lands. On error the value is a
            dash, the same convention EvidenceProvenance uses for a field it does not hold, rather
            than a second sentence repeating what the prose above already said.

            `marginTop: auto` pins it to the foot of whatever height the card ends up with. Two
            cards in a row stretch to the taller of them, and without this the shorter card's
            timestamp floats up under its own prose while its neighbour's sits at the bottom, so
            the pair reads as two differently built cards rather than one row. */}
        <Stack sx={(t: Theme) => ({ marginTop: 'auto', gap: t.semantic.spacing['3xs'] })}>
          <Typography variant="caption" color="text.secondary">
            Last updated on
          </Typography>
          {isLoading ? (
            <Skeleton variant="text" width="40%" />
          ) : (
            <Typography variant="body2" color="text.secondary">
              {/* `formatToDDMMMYYYYHHMMAAA` falls back to "N/A" for an absent value, which would
                  contradict the dash convention two lines up. The dash is chosen explicitly for
                  every case where there is no timestamp, not only for `error`. */}
              {isError || !lastUpdatedOn
                ? MISSING_TIMESTAMP
                : formatToDDMMMYYYYHHMMAAA(lastUpdatedOn)}
            </Typography>
          )}
        </Stack>
      </Stack>

      {/* Beside the content, not below it, which is what puts the thing judged next to the
          judgement: a client reads the capture and the statement about it together.

          `flex-grow: 0` so the capture keeps its natural width and the prose column absorbs the
          rest of the card. Left to grow, the media column takes half the card and the statement
          wraps to a word a line.

          **Narrowed from `flex: 'none'` (flex-shrink: 0), not left as it was.** Blocking growth
          also blocked shrinking, which held the media column at its 116px preferred width no
          matter how narrow the card got, so `EvidenceThumbnail`'s own `max-width: 100%` plus
          `aspect-ratio` shrink rule never had a narrower box to shrink into and the tile overflowed
          the card at mobile widths (A4's acceptance criterion; caught by
          `CustomerConfidenceCards.stories.tsx`'s `ThumbnailShrinksInsteadOfOverflowingItsCard`).
          `flex: '0 1 auto'` keeps the no-growth half of the original reasoning and drops the
          no-shrink half. `minWidth: 0` is needed alongside it for the same reason `CardContainer`
          and the prose column both carry it above: a flex item's default `min-width: auto` refuses
          to shrink below its content's own size regardless of `flex-shrink`.

          Rendered only at `ready`, alongside the actions and for the same reason: with no level
          there is nothing to show evidence of. */}
      {status === 'ready' && media && (
        <Stack sx={{ flex: '0 1 auto', minWidth: 0, alignSelf: 'stretch' }}>{media}</Stack>
      )}
    </CardContainer>
  );
}
